import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  SegmentedControl,
  Space,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconSearch, IconMessageCheck, IconMessageX, IconMessageOff } from "@tabler/icons-react";
import { fetchPropertyBillingAsync } from "../../actions/billing.actions";
import CustomTable from "../../components/tables/CustomTable";
import { formatDate } from "../../utils/date";
import { formatNumberWithCommas } from "../../utils/numbers";
import { BillingResponseType, MetersReturnType, TransactionType } from "../../types";

type SmsFilter = "all" | "sent" | "no_contact";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

type BillingViewProps = {
  propertyId: string | null;
  meters: MetersReturnType;
};

export default function BillingView({ propertyId, meters }: BillingViewProps) {
  const [transactions, setTransactions] = useState<TransactionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [smsFilter, setSmsFilter] = useState<SmsFilter>("all");

  const fetchTransactions = async (pageNum: number, pageLimit = limit) => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const result = await fetchPropertyBillingAsync(
        propertyId,
        pageNum,
        pageLimit
      );
      if (result && (result as any).data) {
        const response = (result as any).data as BillingResponseType;
        setTransactions(response.data);
        setPage(response.currentPage);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const handleLimitChange = (value: string | null) => {
    if (!value) return;
    const newLimit = Number(value);
    setLimit(newLimit);
    setPage(1);
    fetchTransactions(1, newLimit);
  };

  const noContactCount = transactions.filter((tx) => tx.smsSent === null || tx.smsSent === undefined).length;
  const smsSentCount   = transactions.filter((tx) => tx.smsSent === true).length;

  const filteredTransactions = transactions.filter((tx) => {
    const matchesSms =
      smsFilter === "all" ? true :
      smsFilter === "sent" ? tx.smsSent === true :
      /* no_contact */ (tx.smsSent === null || tx.smsSent === undefined || tx.smsSent === false);

    if (!matchesSms) return false;
    if (!search.trim()) return true;

    const q = search.toLowerCase();
    return (
      tx.firstName?.toLowerCase().includes(q) ||
      tx.msisdn?.toLowerCase().includes(q) ||
      tx.billRefNumber?.toLowerCase().includes(q) ||
      String(tx.transAmount).includes(q)
    );
  });

  const headers = [
    { label: "Date / Time", accessor: "dateTime" },
    { label: "Payer", accessor: "payer" },
    { label: "Phone", accessor: "phone" },
    { label: "Meter No.", accessor: "meterNo" },
    { label: "Amount (KES)", accessor: "amount" },
    { label: "Liters Credited", accessor: "liters" },
    { label: "Status", accessor: "status" },
    { label: "SMS", accessor: "sms" },
    { label: "Error", accessor: "error" },
  ];

  const rows = filteredTransactions.map((tx) => {
    const truncated = Math.trunc((tx.litersCredited || 0) * 100) / 100;
    const [whole, dec] = truncated.toFixed(2).split(".");

    return {
      id: tx.id,
      dateTime: (
        <Text size="sm">{tx.transTime ? formatDate(tx.transTime) : "--"}</Text>
      ),
      payer: <Text size="sm">{tx.firstName || "--"}</Text>,
      phone: (
        <Text size="xs" c="dimmed" truncate="end" maw={80}>
          {tx.msisdn || "--"}
        </Text>
      ),
      meterNo: (() => {
        const meterLabel = (meters || []).find(
          (m) => m.meterSerialNo === tx.billRefNumber
        )?.labelDetails;
        return (
          <Box>
            <Text size="sm">{tx.billRefNumber || "--"}</Text>
            {meterLabel && (
              <Text size="xs" c="dimmed" fz={10}>
                {meterLabel}
              </Text>
            )}
          </Box>
        );
      })(),
      amount: (
        <Text size="sm" fw={600}>
          {formatNumberWithCommas(Number(tx.transAmount))}
        </Text>
      ),
      liters: (
        <Group gap={0}>
          <Text size="sm" fw={700}>
            {formatNumberWithCommas(Number(whole))}
          </Text>
          <Text size="xs" fw={400} c="dimmed">
            .{dec}
          </Text>
        </Group>
      ),
      status: tx.processingError ? (
        <Badge color="red" variant="light">
          Failed
        </Badge>
      ) : (
        <Badge color="green" variant="light">
          Success
        </Badge>
      ),
      sms: (() => {
        if (tx.smsSent === true) {
          return (
            <Tooltip label="SMS sent to customer" withArrow>
              <Badge color="teal" variant="light" leftSection={<IconMessageCheck size={12} />}>
                Sent
              </Badge>
            </Tooltip>
          );
        }
        if (tx.smsSent === false) {
          return (
            <Tooltip label="Contact found but SMS delivery failed" withArrow>
              <Badge color="orange" variant="light" leftSection={<IconMessageX size={12} />}>
                Failed
              </Badge>
            </Tooltip>
          );
        }
        return (
          <Tooltip label="No contact record — add phone number to contacts table" withArrow>
            <Badge color="gray" variant="light" leftSection={<IconMessageOff size={12} />}>
              No Contact
            </Badge>
          </Tooltip>
        );
      })(),
      error: tx.processingError ? (
        <Text size="xs" c="red">
          {tx.processingError}
        </Text>
      ) : (
        <Text size="xs" c="dimmed">
          --
        </Text>
      ),
    };
  });

  return (
    <Box>
      <Group justify="space-between" align="center">
        <Text c="#03045e" size="xl" fw={800}>
          Billing
        </Text>
        <TextInput
          placeholder="Search by payer, phone, meter no., amount..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={320}
          size="sm"
        />
      </Group>
      <Space h="sm" />
      <Group justify="space-between" align="center">
        <SegmentedControl
          size="xs"
          value={smsFilter}
          onChange={(v) => setSmsFilter(v as SmsFilter)}
          data={[
            { label: "All", value: "all" },
            { label: `SMS Sent (${smsSentCount})`, value: "sent" },
            { label: `No Contact (${noContactCount})`, value: "no_contact" },
          ]}
        />
        {smsFilter === "no_contact" && noContactCount > 0 && (
          <Text size="xs" c="orange">
            {noContactCount} transaction{noContactCount !== 1 ? "s" : ""} with no contact linked — add their phone numbers to the contacts table to enable SMS.
          </Text>
        )}
      </Group>
      <Space h="sm" />
      {loading ? (
        <Group justify="center" my="md">
          <Loader size="sm" />
          <Text>Loading...</Text>
        </Group>
      ) : !transactions.length ? (
        <Paper py="md" radius="md">
          <Text ta="center" fw={600}>
            No transactions for this property.
          </Text>
        </Paper>
      ) : !filteredTransactions.length ? (
        <Paper py="md" radius="md">
          <Text ta="center" fw={600} c="dimmed">
            No transactions match your search.
          </Text>
        </Paper>
      ) : (
        <>
          <CustomTable headers={headers} rows={rows} />
          <Space h="md" />
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed">Rows per page</Text>
              <Select
                data={PAGE_SIZE_OPTIONS}
                value={String(limit)}
                onChange={handleLimitChange}
                w={70}
                size="xs"
                allowDeselect={false}
              />
            </Group>
            <Text size="sm" c="dimmed">
              {total === 0
                ? "0 results"
                : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`}
            </Text>
            <Pagination
              total={totalPages}
              value={page}
              onChange={(p) => fetchTransactions(p)}
              size="sm"
            />
          </Group>
        </>
      )}
    </Box>
  );
}
